import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSSETaskMonitor } from '../../hooks/useSSETaskMonitor';
import ProgressLoader from '../common/ProgressLoader';
import { ENDPOINTS } from '../../api/endPoints';
import { motion } from 'framer-motion';
import { resetCampaignsCache } from '../../redux/slices/campaignsSlice';

interface CampaignPostingProgressProps {
  requestId: string;
  restaurantId: string;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

const CampaignPostingProgress: React.FC<CampaignPostingProgressProps> = ({
  requestId,
  restaurantId,
  onComplete,
  onError
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showSuccess, setShowSuccess] = useState(false);
  const hasCompletedRef = useRef(false);

  const monitorUrl = ENDPOINTS.sseMonitor(requestId);

  const handleComplete = useCallback(() => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    
    onComplete?.();
    dispatch(resetCampaignsCache());
    setShowSuccess(true);
    setTimeout(() => {
      navigate(`/${restaurantId}/marketing-calendar`);
    }, 2000);
  }, [onComplete, dispatch, navigate, restaurantId]);

  const { percentCompleted, message, status } = useSSETaskMonitor({
    monitorUrl,
    enabled: true,
    onCompleted: handleComplete,
    onFailed: (error) => {
      console.error('Campaign posting failed:', error);
      onError?.(error);
      navigate(`/${restaurantId}/marketing-calendar`);
    }
  });

  // Watch for 100% completion as fallback
  useEffect(() => {
    if (percentCompleted >= 100 && !hasCompletedRef.current) {
      handleComplete();
    }
  }, [percentCompleted, handleComplete]);

  return (
    <ProgressLoader
      percentage={showSuccess ? 100 : percentCompleted}
      message={showSuccess ? '✓ Campaign Posted Successfully! Redirecting...' : (message || 'Posting campaign...')}
      title="Posting to Social Media"
      success={showSuccess}
    />
  );
};

export default CampaignPostingProgress;
